import * as React from 'react';
import cn from 'classnames';
import { Trans } from '@lingui/macro';
import { FocusEventHandler, ChangeEventHandler } from 'react';

import Message, { THEME } from '@mos-cat/message';
import Select from '@mos-front-components/public/es/components/MosSelect';
import { Field } from '../../../declarations/widget-ums';
import TextField from '../TextField/TextField';
import {
  BIRTH_CERTIFICATE_RF_PATTERN,
  PASSPORT_RF_PATTERN,
  BIRTH_CERTIFICATE_INTERNATIONAL_PATTERN,
} from '../../../constants/REGEXP_PATTERNS';
import {
  ADULT_DOCUMENT_OPTIONS,
  CHILD_DOCUMENT_OPTIONS,
  DOCUMENT_FIELD,
  DOCUMENT_TYPE,
  DocumentTypeValue,
} from '../../../constants/document';
import { CITIZENSHIP } from '../../../constants/citizenship';
import './Document.pcss';
import BirthPlace from '../BirthPlace/BirthPlace';
import { connect } from 'dva';
import { getPersonPlaceholder } from '../../../helpers/getPlacelholder';
import {
  maxAcceptableDate,
  minAcceptableDate,
} from '../../../helpers/dateHelpers';
import { ERROR_DATE } from '../../../constants/errors';
import { setErrorToRelativeFields } from '../../../helpers/handlers';
import DateField from '../DateField/DateField';
import { DOCUMENT_FIELD_NAME } from '../../../constants/fieldNames';
import { getNewKey } from '../../../helpers/getNewKey';
import TextFieldHelper from '../TextFieldHelper/TextFieldHelper';
import { CHILD } from '../../../models/child';

interface CommonProps {
  citizenship: CITIZENSHIP | null;
  type: Field<DocumentTypeValue>;
  number: Field;
  issueDate: Field;
  issuer: Field;
  model: string;
  documentField?: DOCUMENT_FIELD;
}

type OptionalProps =
  | {
      birthCertificate: false;
      divisionCode: Field;
      birthPlace: Field;
    }
  | {
      birthCertificate: true;
      divisionCode?: never;
      birthPlace?: never;
    };

export type DocumentProps = {
  onChange: ChangeEventHandler;
  onBlur: FocusEventHandler;
  onDocumentTypeChange(
    val: { value: DOCUMENT_TYPE },
    model,
    documentField
  ): void;
  showBirthPlace?: boolean;
} & CommonProps &
  OptionalProps;

const isRussianPassportTypeValue = (type) =>
  type.value === DOCUMENT_TYPE.PASSPORT_RF;
const isRussianBirthCertificateTypeValue = (type) =>
  type.value === DOCUMENT_TYPE.BIRTH_CERTIFICATE_RF;

function Document({
  birthCertificate = false,
  onChange,
  onBlur,
  ...props
}: DocumentProps) {
  const {
    type,
    model,
    birthPlace,
    showBirthPlace,
    citizenship,
    issueDate,
    onDocumentTypeChange,
  } = props;
  if (citizenship === null && !birthCertificate) return null;

  const suggestions = birthCertificate
    ? CHILD_DOCUMENT_OPTIONS
    : ADULT_DOCUMENT_OPTIONS[citizenship as CITIZENSHIP];

  const isRussianPassport = isRussianPassportTypeValue(type);
  const isRussianBirthCertificate = isRussianBirthCertificateTypeValue(type);
  const isInternationalBirthCertificate =
    type.value === DOCUMENT_TYPE.BIRTH_CERTIFICATE_INTERNATIONAL;

  let numberMask;
  if (isRussianPassport) {
    numberMask = PASSPORT_RF_PATTERN.FOR_INPUT_MASK;
  }
  if (isRussianBirthCertificate) {
    numberMask = BIRTH_CERTIFICATE_RF_PATTERN.FOR_INPUT_MASK;
  }
  if (isInternationalBirthCertificate) {
    numberMask = BIRTH_CERTIFICATE_INTERNATIONAL_PATTERN.FOR_INPUT_MASK;
  }

  const value = (suggestions.length === 1
    ? suggestions[0]
    : suggestions.find(({ value }) => value === type.value) || {
        text: '',
      }
  ).text;

  const typePlaceholder = birthCertificate
    ? type.value
      ? getPersonPlaceholder('birthCertificate').full
      : getPersonPlaceholder('birthCertificate').empty
    : getPersonPlaceholder('document');

  const issueDateErrorLocation =
    typeof issueDate.error === 'string' &&
    [
      ERROR_DATE.DOCUMENT_DATE_EARLY_BIRTHDATE,
      ERROR_DATE.DOCUMENT_TOO_OLD,
      birthCertificate ? '' : ERROR_DATE.DOCUMENT_DATE_EARLY_THAN_14,
    ].includes(issueDate.error)
      ? 'tooltip'
      : 'placeholder';

  const isInternationalBirthCertificateSelected =
    isInternationalBirthCertificate && !props.number.disabled;
  return (
    <>
      <div
        className="PguFormGrid__row"
        key={model === 'child' ? getNewKey(props.number.value) : model}
      >
        <div className="PguFormGrid_col PguFormGrid__col_5">
          <Select
            dropListView
            className="PguFormGrid__field"
            suggestions={suggestions}
            disabled={type.disabled || suggestions.length === 1}
            hasError={type.error}
            placeholder={typePlaceholder}
            value={value}
            onChange={onDocumentTypeChange}
            name={DOCUMENT_FIELD_NAME.type}
          />
        </div>
      </div>

      {isInternationalBirthCertificateSelected && (
        <Message theme={THEME.ATTENTION}>
          <Trans>
            Вам необходимо указать сведения в соответствии с нотариально
            заверенным переводом
          </Trans>
        </Message>
      )}

      {type.value && (
        <>
          <div className="PguFormGrid__row">
            <div className="PguFormGrid_col TextFieldHelperWrapper">
              <TextField
                key={type.value}
                className="PguFormGrid__field"
                placeholder={getPersonPlaceholder('seriesAndNumber')}
                maskOptions={
                  numberMask && {
                    regex: numberMask as string,
                    casing: isRussianBirthCertificate ? 'upper' : undefined,
                    showMaskOnHover: false,
                    placeholder: isInternationalBirthCertificate ? '' : '_',
                  }
                }
                inputProps={{
                  maxLength: isRussianBirthCertificate ? 20 : 50,
                  ...props.number,
                  onChange,
                  onBlur,
                  name: DOCUMENT_FIELD_NAME.number,
                  autoCapitalize: isRussianBirthCertificate
                    ? 'characters'
                    : 'off',
                }}
                icon={
                  isInternationalBirthCertificateSelected ? (
                    <div style={{ width: 30 }} />
                  ) : null
                } // for correct margin to TextFieldHelper icon
              />
              {isInternationalBirthCertificateSelected && (
                <TextFieldHelper
                  key={'isInternationalBirthCertificate'}
                  zIndex={1031}
                >
                  <div>
                    Серия (при наличии) с номером вводятся через&nbsp;пробел
                  </div>
                </TextFieldHelper>
              )}
            </div>
            <div className="PguFormGrid_col PguFormGrid__col_3">
              <DateField
                key="issueDate"
                className="PguFormGrid__field"
                placeholder={getPersonPlaceholder(
                  DOCUMENT_FIELD_NAME.issueDate
                )}
                onChange={onChange}
                onBlur={onBlur}
                name={DOCUMENT_FIELD_NAME.issueDate}
                {...issueDate}
                minDate={minAcceptableDate}
                maxDate={maxAcceptableDate}
                errorLocation={issueDateErrorLocation}
              />
            </div>
          </div>
          <div className="PguFormGrid__row">
            <div
              className={cn('PguFormGrid_col', {
                PguFormGrid__col_5: !isRussianPassport,
              })}
            >
              <TextField
                key="issuer"
                className="PguFormGrid__field"
                placeholder={getPersonPlaceholder(DOCUMENT_FIELD_NAME.issuer)}
                inputProps={{
                  ...props.issuer,
                  onChange,
                  onBlur,
                  name: DOCUMENT_FIELD_NAME.issuer,
                  maxLength: 500,
                }}
              />
            </div>
            {isRussianPassport && (
              <div className="PguFormGrid_col PguFormGrid__col_3">
                <TextField
                  key="divisionCode"
                  className="PguFormGrid__field"
                  maskOptions={{ mask: '9{3}-9{3}', showMaskOnHover: false }}
                  placeholder={getPersonPlaceholder(
                    DOCUMENT_FIELD_NAME.divisionCode
                  )}
                  inputProps={{
                    name: DOCUMENT_FIELD_NAME.divisionCode,
                    onBlur,
                    onChange,
                    ...props.divisionCode,
                  }}
                />
              </div>
            )}
          </div>
          {showBirthPlace && (
            <BirthPlace model={model} birthPlace={birthPlace} />
          )}
        </>
      )}
    </>
  );
}

function mapDispatchToProps(dispatch, props) {
  const { model, birthCertificate, type } = props;
  const isRussianPassport = isRussianPassportTypeValue(type);
  const isRussianBirthCertificate = isRussianBirthCertificateTypeValue(type);

  const getDocumentField = (birthCertificate) =>
    DOCUMENT_FIELD[`${birthCertificate ? 'birthCertificate' : 'dul'}`];

  const dispatchValidateField = ({ name, value }) =>
    dispatch({
      type: `${model}/validate/field`,
      payload: { namespace: model, name, value },
      additionalNamespace: birthCertificate ? 'birthCertificate' : 'dul',
    });

  const dispatchSetValues = ({ name, value }) =>
    dispatch({
      type: `${model}/document/set/values`,
      payload: { [name]: value },
      documentField: getDocumentField(birthCertificate),
    });

  const dispatchDocumentValidate = ({ name, value }) =>
    dispatch({
      type: `${model}/document/validate`,
      payload: { [name]: value },
      documentField: getDocumentField(birthCertificate),
    });

  const dispatchSaveDraft = () => {
    if (!model.includes('child')) {
      dispatch({ type: 'form/saveDraft' });
    }
  };

  const dispatchSetTrimmedValuesAndSaveDraft = ({ name, value }) => {
    const trimmedValue = value.trim();
    dispatch({
      type: `${model}/document/set/values`,
      payload: { [name]: trimmedValue },
      documentField: getDocumentField(birthCertificate),
    });

    dispatchSaveDraft();
  };

  return {
    onChange({ target: { name, value } }) {
      if (name === DOCUMENT_FIELD_NAME.issueDate) {
        setErrorToRelativeFields(dispatch, model, ['birthdate']);
        dispatch({
          type: `${model}/reset/passport/status`,
          payload: { namespace: model },
        });
      }
      dispatchSetValues({ name, value });
    },
    onDocumentTypeChange({ value }) {
      dispatchSetValues({ name: 'type', value });

      if (birthCertificate) {
        dispatchDocumentValidate({ name: 'type', value });
        dispatch({
          type: `${model}/set/birthCertificate/input_type`,
          payload: 'LK',
        });
      }
      dispatchSaveDraft();
    },
    onBlur(event) {
      const { target } = event;
      const { name } = target;

      dispatchDocumentValidate(target);

      if (name === DOCUMENT_FIELD_NAME.number && model === CHILD) {
        dispatch({
          type: `${model}/set/birthCertificate/input_type`,
          payload: 'manual_input',
        });
      }

      if (name === DOCUMENT_FIELD_NAME.number) {
        if (isRussianPassport || isRussianBirthCertificate) {
          return dispatchValidateField(target);
        }
        return dispatchSetTrimmedValuesAndSaveDraft(target);
      }

      if (
        [
          DOCUMENT_FIELD_NAME.issueDate,
          DOCUMENT_FIELD_NAME.divisionCode,
        ].includes(name)
      ) {
        dispatchValidateField(target);
      }
      if (name === DOCUMENT_FIELD_NAME.issueDate) {
        dispatch({
          type: `${model}/passport/validity`,
        });
      }
      if ([DOCUMENT_FIELD_NAME.issuer].includes(name)) {
        dispatchSetTrimmedValuesAndSaveDraft(target);
      }
    },
  };
}

export default connect(() => ({}), mapDispatchToProps)(Document);
